import { useEffect, useState } from 'react'
import { Link, useLocation, useNavigate, useParams } from 'react-router-dom'
import { useAuth } from '../auth/AuthContext'
import { useLocale } from '../../shared/i18n/LocaleContext'
import { getProductionOrder, orderCommand } from './productionOrdersApi'
import { ProductionOrderDetail } from './ProductionOrdersPage'
import { AppModal } from '../../shared/components/AppModal'
import { PageLoading } from '../../shared/components/PageLoading'
import { useAppConfirm } from '../../shared/components/AppConfirmDialog'
import type { ProductionOrdersSnapshot } from './types'

export function ProductionOrderDetailPage() {
  const { orderId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const { user } = useAuth()
  const { t } = useLocale()
  const { confirm, confirmationDialog } = useAppConfirm()
  const [data, setData] = useState<ProductionOrdersSnapshot | null>(null)
  const [error, setError] = useState<string | null>(null)
  const [headerSave, setHeaderSave] = useState<{ run: () => void; disabled: boolean } | null>(null)

  const load = async (signal?: AbortSignal) => {
    try {
      if (!orderId) throw new Error('orders.notFound')
      const snapshot = await getProductionOrder(orderId, signal)
      if (signal?.aborted) return
      setData(snapshot)
      setError(null)
    } catch (reason) {
      if (signal?.aborted) return
      setError(reason instanceof Error ? reason.message : 'orders.loadError')
    }
  }

  useEffect(() => {
    const controller = new AbortController()
    setData(null)
    void load(controller.signal)
    return () => controller.abort()
  }, [user?.activeCompanyId, orderId])

  const order = data?.orders.find((item) => item.id === orderId)
  const article = data?.articles.find((item) => item.id === order?.articleId)
  const routeState = location.state as { returnTo?: string; recordIds?: string[] } | null
  const recordIds = routeState?.recordIds ?? []
  const orderIndex = orderId ? recordIds.indexOf(orderId) : -1
  const returnTo = routeState?.returnTo?.startsWith('/app/ordenes-fabricacion/listado')
    ? routeState.returnTo : '/app/ordenes-fabricacion/listado'
  const command = async (path: string, body: unknown) => {
    if (!order) return
    try {
      const result = await orderCommand(`/${order.id}${path}`, 'PUT', body)
      await load()
      if (result.planningScenarioRequired && user?.permissions.planning === 'Write' &&
        await confirm(t('planning.changeRequiresSimulation')))
        navigate('/app/planificacion?scenario=global')
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'orders.actionError')
    }
  }

  if (!data) return <PageLoading label={t('orders.loading')}
    error={error ? t(error as Parameters<typeof t>[0]) : null}/>
  if (!order) return <main className="page-content"><p className="form-error">{t('orders.notFound')}</p><Link className="secondary-button" to={returnTo}>{t('orders.backToList')}</Link></main>

  return <main className="page-content">
    <AppModal title={`${order.number} · ${article?.code ?? ''}`} closeLabel={t('nav.closeMenu')}
      onClose={() => navigate(returnTo)} wide
      onPrevious={orderIndex > 0 ? () => navigate(`/app/ordenes-fabricacion/${recordIds[orderIndex - 1]}`, {state:routeState}) : null}
      onNext={orderIndex >= 0 && orderIndex < recordIds.length - 1 ? () => navigate(`/app/ordenes-fabricacion/${recordIds[orderIndex + 1]}`, {state:routeState}) : null}
      onSave={user?.permissions.productionOrders === 'Write' ? headerSave?.run : null}
      saveDisabled={headerSave?.disabled ?? true}
      feedback={error ? t(error as Parameters<typeof t>[0]) : null}>
      <ProductionOrderDetail order={order} data={data}
        canEdit={user?.permissions.productionOrders === 'Write'} command={command}
        onHeaderSaveChange={setHeaderSave} />
    </AppModal>
    {confirmationDialog}
  </main>
}
